/**
 * Sprint 15 — Contact Form Email → Begutachtung parser.
 *
 * Reads the HTML DOM of an incoming e-mail and extracts common German contact-form fields.
 * Robust against layout changes: works on tables, definition lists, paragraphs, and inline labels.
 *
 * Returns:
 *   {
 *     name, email, phone,
 *     car_model, color, plate, year,
 *     service_name, notes,
 *     recognized: string[],   // field keys that were extracted
 *     missing: string[],      // known fields that could NOT be recognized
 *   }
 */

// Field keyword dictionary — order matters (more specific first).
// Each entry: { key, patterns: RegExp[] to match the LABEL text (case-insensitive) }
const FIELD_MAP = [
  { key: "email",         patterns: [/^\s*e[\-\s]?mail\b/i, /^\s*mail\b/i] },
  { key: "phone",         patterns: [/^\s*telefon\b/i, /^\s*tel\.?\b/i, /^\s*phone\b/i, /^\s*mobil\b/i, /^\s*handy\b/i] },
  { key: "name",          patterns: [/^\s*(vor.?\s*und\s*)?nach?name\b/i, /^\s*name\b/i, /^\s*kontakt\b/i] },
  // The public website deliberately collects all vehicle details in one compact field.
  // Keep this before the generic "Baujahr" matcher so the whole answer can be split below.
  { key: "vehicle_details", patterns: [
    /^\s*baujahr.*marke.*modell.*farbe/i,
    /^\s*marke.*modell.*farbe.*baujahr/i,
    /^\s*fahrzeugdaten\b/i,
  ] },
  { key: "year",          patterns: [/^\s*baujahr\b/i, /^\s*jahr\b/i, /^\s*ez\b/i] },
  { key: "color",         patterns: [/^\s*farbe\b/i, /^\s*lackfarbe\b/i, /^\s*fahrzeug\s*farbe\b/i] },
  { key: "plate",         patterns: [/^\s*kennzeichen\b/i, /^\s*kfz-?kennzeichen\b/i] },
  { key: "vehicle_brand", patterns: [/^\s*fahrzeugmarke\b/i, /^\s*automarke\b/i, /^\s*marke\b/i] },
  { key: "vehicle_model", patterns: [/^\s*fahrzeugmodell\b/i, /^\s*modell\b/i] },
  { key: "car_model",     patterns: [/^\s*fahrzeug(?!\s*farbe)/i, /^\s*auto(modell)?\b/i, /^\s*modell\b/i, /^\s*marke\s*(und\s*)?modell\b/i, /^\s*marke\b/i, /^\s*pkw\b/i] },
  { key: "service_name",  patterns: [/^\s*(gew(ü|ue)nschte\s*)?leistung\b/i, /^\s*service\b/i, /^\s*dienstleistung\b/i, /^\s*paket\b/i] },
  { key: "notes",         patterns: [/^\s*beschreib/i, /^\s*nachricht\b/i, /^\s*mitteilung\b/i, /^\s*anliegen\b/i, /^\s*bemerkung/i, /^\s*kommentar\b/i, /^\s*notiz/i] },
];

const ALL_FIELD_KEYS = [
  "name", "email", "phone", "vehicle_brand", "vehicle_model", "car_model",
  "color", "year", "plate", "service_name", "notes",
];

// German label — for the "missing" list surfaced to the user
const FIELD_LABELS = {
  name: "Name",
  email: "E-Mail",
  phone: "Telefon",
  car_model: "Fahrzeug",
  color: "Farbe",
  year: "Baujahr",
  plate: "Kennzeichen",
  service_name: "Gewünschte Leistung",
  notes: "Beschreibung / Nachricht",
};

// Which labels should the user always expect from a Contact-Form. If a field is
// NOT present we surface it to the user so they know to fill it in manually.
const EXPECTED_FIELDS = ["name", "email", "phone", "car_model", "service_name"];

const LABEL_STRIP = /\s*[:：]\s*$/;   // trailing colon
const VALUE_STRIP = /^\s*[:：]\s*/;   // leading colon
const BLOCK_TAGS = new Set(["P", "DIV", "LI", "TR", "H1", "H2", "H3", "H4", "H5", "H6", "SECTION", "ARTICLE", "BR"]);

function normalizeWhitespace(s) {
  return (s || "").replace(/\s+/g, " ").trim();
}

const VEHICLE_COLOR_WORDS = [
  "schwarz", "weiß", "weiss", "silber", "grau", "anthrazit", "blau", "rot",
  "grün", "gruen", "braun", "beige", "gelb", "orange", "violett", "gold",
];

/**
 * Split the website field "Baujahr, Marke, Modell und Farbe des Fahrzeugs?".
 * Typical values:
 *   "2017, Audi Cabrio, grau metallic"
 *   "Smart Fortwo, braun, Bj 2019"
 */
function splitVehicleDetails(raw) {
  const value = normalizeWhitespace(raw);
  if (!value) return {};

  const yearMatch = value.match(/\b(?:19|20)\d{2}\b/);
  const year = yearMatch?.[0] || "";
  const withoutYear = value
    .replace(/\b(?:bj\.?|baujahr|ez)\s*[:\-]?\s*(?:19|20)\d{2}\b/ig, "")
    .replace(/\b(?:19|20)\d{2}\b/g, "")
    .replace(/^[\s,;|/-]+|[\s,;|/-]+$/g, "");

  const parts = withoutYear.split(/\s*[,;|]\s*/).map(normalizeWhitespace).filter(Boolean);
  const colorPattern = new RegExp(`\\b(${VEHICLE_COLOR_WORDS.join("|")})\\b`, "i");
  let colorIndex = parts.findIndex((part) => colorPattern.test(part));
  if (colorIndex < 0 && parts.length > 1 && /\b(metallic|matt|metalliclack)\b/i.test(parts.at(-1))) {
    colorIndex = parts.length - 1;
  }

  const color = colorIndex >= 0 ? parts[colorIndex] : "";
  const carParts = parts.filter((_, index) => index !== colorIndex);
  const car_model = normalizeWhitespace(carParts.join(" "));

  return { year, color, car_model };
}

/** Match a label string against the FIELD_MAP → returns the field key or null. */
function matchLabel(rawLabel) {
  const label = (rawLabel || "").replace(LABEL_STRIP, "").trim();
  if (!label) return null;
  for (const entry of FIELD_MAP) {
    for (const pat of entry.patterns) {
      if (pat.test(label)) return entry.key;
    }
  }
  return null;
}

/**
 * Walk the DOM in document order and produce a list of "cells" — every table-cell,
 * list item, paragraph, or line break becomes a boundary. This preserves adjacency
 * information (label followed by value on the next cell), which is how most
 * German contact-form emails are laid out (table with 2 columns or bold label + text).
 */
function extractCells(root) {
  const cells = [];
  // Special-case tables: for every <tr>, iterate <td> children as separate cells with a
  // "sibling" marker linking label→value across cells.
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let buffer = "";
  const flush = () => {
    const clean = normalizeWhitespace(buffer);
    if (clean) cells.push(clean);
    buffer = "";
  };
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      buffer += " " + (node.nodeValue || "");
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName;
      // TD/TH/DT/DD are cells on their own — flush before AND after.
      if (["TD", "TH", "DT", "DD"].includes(tag)) {
        flush();
      } else if (BLOCK_TAGS.has(tag)) {
        flush();
      }
    }
    node = walker.nextNode();
  }
  flush();
  return cells;
}

/** Try to split "Label: Value" from a single cell. Returns null if no colon separator. */
function splitInline(cell) {
  // Match "Label: value" with the label being non-greedy up to the FIRST colon.
  const m = cell.match(/^([^:：]{1,40})[:：]\s*(.+)$/);
  if (!m) return null;
  return { label: m[1].trim(), value: m[2].trim() };
}

/**
 * Main entry. Accepts either raw HTML string OR a pre-parsed document.
 * Returns a shallow map of extracted fields plus recognized/missing arrays.
 */
export function parseContactFormEmail(htmlOrDoc) {
  const result = { recognized: [], missing: [] };
  if (!htmlOrDoc) {
    result.missing = EXPECTED_FIELDS.slice();
    return result;
  }

  let doc;
  if (typeof htmlOrDoc === "string") {
    // The user's mail HTML may be a fragment or a full document — DOMParser handles both.
    const parser = new DOMParser();
    doc = parser.parseFromString(htmlOrDoc, "text/html");
  } else {
    doc = htmlOrDoc;
  }
  const root = doc.body || doc.documentElement;
  if (!root) {
    result.missing = EXPECTED_FIELDS.slice();
    return result;
  }

  const cells = extractCells(root);

  // Pass 1 — inline "Label: value" cells
  const consumed = new Set();
  for (let i = 0; i < cells.length; i++) {
    const inline = splitInline(cells[i]);
    if (!inline) continue;
    const key = matchLabel(inline.label);
    if (!key) continue;
    if (!result[key]) {
      result[key] = normalizeWhitespace(inline.value.replace(VALUE_STRIP, ""));
      consumed.add(i);
    }
  }

  // Pass 2 — adjacent cells (label alone in one cell, value in the next)
  for (let i = 0; i < cells.length - 1; i++) {
    if (consumed.has(i)) continue;
    const label = cells[i];
    if (!label || label.length > 40) continue;
    const key = matchLabel(label);
    if (!key) continue;
    if (result[key]) continue;
    // Look ahead for the first non-empty, non-label cell
    for (let j = i + 1; j < Math.min(i + 4, cells.length); j++) {
      if (consumed.has(j)) continue;
      const value = normalizeWhitespace(cells[j].replace(VALUE_STRIP, ""));
      if (!value) continue;
      // Skip only when the candidate value is a SHORT bare label AND we are NOT capturing
      // a free-text field ("notes"). Descriptions can start with any word (e.g. "Auto ist
      // stark verkratzt…") and must not be false-rejected as another label.
      if (key !== "notes" && value.length < 40 && matchLabel(value)) continue;
      result[key] = value;
      consumed.add(j);
      break;
    }
  }

  // Note: no tail-glue post-processing. The primary Pass 1/2 already capture the description
  // correctly for the two most common layouts (inline "Beschreibung: text" and adjacent-cell
  // tables). A tail-collection heuristic would incorrectly absorb unknown labels that follow
  // the description row (e.g. "Herkunft: Google"). Per spec, unknown fields must be silently
  // dropped, so we intentionally do not try to be clever here.

  // Expand the compact vehicle answer used by auto-stellar.de into the fields expected
  // by the appointment form. Explicit individual fields, when present, take precedence.
  if (result.vehicle_details) {
    const vehicle = splitVehicleDetails(result.vehicle_details);
    if (!result.year && vehicle.year) result.year = vehicle.year;
    if (!result.color && vehicle.color) result.color = vehicle.color;
    if (!result.car_model && vehicle.car_model) result.car_model = vehicle.car_model;
    delete result.vehicle_details;
  }
  // WPForms now sends brand and model as separate fields. Keep both exact values
  // and also provide the legacy combined car_model used by older ERP screens.
  if ((result.vehicle_brand || result.vehicle_model) && !result.car_model) {
    result.car_model = [result.vehicle_brand, result.vehicle_model].filter(Boolean).join(" ");
  }

  // Compute recognized/missing (only for fields actually extracted, not "" and not undefined)
  for (const k of ALL_FIELD_KEYS) {
    if (result[k]) result.recognized.push(k);
  }
  result.missing = EXPECTED_FIELDS.filter((k) => !result.recognized.includes(k));

  return result;
}

export const CONTACT_FIELD_LABELS = FIELD_LABELS;
